import { intersection, isEqual, uniq } from "lodash-es";

import { useQueueCases } from "src/cases/queries/queues";
import { useCaseTemplateVersion } from "src/cases/queries/templates";
import { useCasesQueueParams } from "src/cases/queues/hooks/useCasesQueueParams";
import { DeserializedQueueCase } from "src/cases/queues/types";
import {
  CaseTemplateActionOutput,
  CaseTemplateVersionAttributes,
  CaseTemplateWorkflowRuleOutput,
} from "src/clients/case-management";
import { useCapabilities } from "src/hooks/useCapabilities";
import { Environment } from "src/hooks/useEnvironment";
import { useCurrentUserId } from "src/store/AuthStore";

export enum BulkActionError {
  TOO_MANY_CASES = "TOO_MANY_CASES",
  DIFFERENT_TEMPLATE_VERSIONS = "DIFFERENT_TEMPLATE_VERSIONS",
  OUTDATED_CASES = "OUTDATED_CASES",
  NO_COMMON_ACTIONS = "NO_COMMON_ACTIONS",
  UNASSIGNED_CASES = "UNASSIGNED_CASES",
}

export const BULK_ACTION_ERROR_MESSAGES: Record<BulkActionError, string> = {
  [BulkActionError.TOO_MANY_CASES]:
    "Custom bulk actions can only be applied up to 50 cases.",
  [BulkActionError.DIFFERENT_TEMPLATE_VERSIONS]:
    "The cases are not created based on the same case template version.",
  [BulkActionError.OUTDATED_CASES]: "One or more selected cases are outdated.",
  [BulkActionError.NO_COMMON_ACTIONS]:
    "The selected cases do not share a common action.",
  [BulkActionError.UNASSIGNED_CASES]:
    "One or more selected cases aren't assigned to you. Only admins or assignees of all selected cases can perform this action.",
};

type TemplateVersionInfo = {
  templateId: string;
  versionId: string;
};

const MAX_BULK_ACTION_CASES = 50;

const createBaseState = (
  overrides: Partial<BulkActionsState>,
  selectedCases: DeserializedQueueCase[] = [],
  templateVersionInfo: TemplateVersionInfo | null = null,
): BulkActionsState => ({
  isLoading: false,
  error: null,
  errorMessage: null,
  commonActions: [],
  canPerformBulkActions: false,
  selectedCases,
  templateVersionId: templateVersionInfo?.versionId ?? null,
  templateId: templateVersionInfo?.templateId ?? null,
  ...overrides,
});

const createErrorState = (
  error: BulkActionError,
  selectedCases: DeserializedQueueCase[],
  templateVersionInfo: TemplateVersionInfo | null = null,
): BulkActionsState =>
  createBaseState(
    {
      error,
      errorMessage: BULK_ACTION_ERROR_MESSAGES[error],
    },
    selectedCases,
    templateVersionInfo,
  );

const extractTemplateVersionInfo = (
  cases: DeserializedQueueCase[],
): TemplateVersionInfo | null => {
  const templateVersionIds = cases.map((c) => c.template_version?.id);
  const templateIds = cases.map((c) => c.template?.id);

  if (templateVersionIds.some((id) => !id) || templateIds.some((id) => !id)) {
    return null;
  }

  const uniqueTemplateVersionIds = uniq(templateVersionIds);
  const uniqueTemplateIds = uniq(templateIds);

  return uniqueTemplateVersionIds.length === 1 && uniqueTemplateIds.length === 1
    ? {
        templateId: uniqueTemplateIds[0]!,
        versionId: uniqueTemplateVersionIds[0]!,
      }
    : null;
};

export type CommonBulkAction = {
  key: string;
  display_name: string;
  description: string;
  action: CaseTemplateActionOutput;
};

export type BulkActionsState = {
  isLoading: boolean;
  error: BulkActionError | null;
  errorMessage: string | null;
  commonActions: CommonBulkAction[];
  canPerformBulkActions: boolean;
  selectedCases: DeserializedQueueCase[];
  templateVersionId: string | null;
  templateId: string | null;
};

type UseBulkCaseActionsParams = {
  selectedCaseIds: string[];
  queueId: string;
  environment: Environment;
};

export const useBulkCaseActions = ({
  selectedCaseIds,
  queueId,
  environment,
}: UseBulkCaseActionsParams): BulkActionsState => {
  const { filterParams: filters } = useCasesQueueParams(queueId);
  const { cases: casesCapabilities } = useCapabilities();
  const currentUserId = useCurrentUserId();

  const { data: allCases, isLoading: isLoadingCases } = useQueueCases({
    queueId,
    environment,
    filters,
  });

  const selectedCases =
    allCases?.filter((c) => selectedCaseIds.includes(c.id)) ?? [];

  const templateVersionInfo = extractTemplateVersionInfo(selectedCases);

  const { data: templateVersionData, isLoading: isLoadingTemplate } =
    useCaseTemplateVersion(
      {
        templateId: templateVersionInfo?.templateId,
        versionId: templateVersionInfo?.versionId,
      },
      {
        enabled: !!templateVersionInfo,
      },
    );

  const bulkActionsState = ((): BulkActionsState => {
    if (isLoadingCases) {
      return createBaseState({ isLoading: true });
    }

    if (!selectedCases.length) {
      return createBaseState({});
    }

    if (selectedCases.length > MAX_BULK_ACTION_CASES) {
      return createErrorState(BulkActionError.TOO_MANY_CASES, selectedCases);
    }

    if (!templateVersionInfo) {
      return createErrorState(
        BulkActionError.DIFFERENT_TEMPLATE_VERSIONS,
        selectedCases,
      );
    }

    if (isLoadingTemplate) {
      return createBaseState(
        { isLoading: true },
        selectedCases,
        templateVersionInfo,
      );
    }

    const templateSchemaVersion =
      templateVersionData?.attributes?.schema_version;
    if (templateSchemaVersion !== undefined) {
      const hasOutdatedCase = selectedCases.some(
        (caseData) => templateSchemaVersion > caseData.template_schema_version,
      );
      if (hasOutdatedCase) {
        return createErrorState(
          BulkActionError.OUTDATED_CASES,
          selectedCases,
          templateVersionInfo,
        );
      }
    }

    if (!casesCapabilities.canManage && currentUserId) {
      const hasUnassignedCases = selectedCases.some(
        (c) => c.assignee?.id !== currentUserId,
      );
      if (hasUnassignedCases) {
        return createErrorState(
          BulkActionError.UNASSIGNED_CASES,
          selectedCases,
          templateVersionInfo,
        );
      }
    }

    const commonActions = extractCommonActions(
      selectedCases,
      templateVersionData,
    );
    if (commonActions.length === 0) {
      return createErrorState(
        BulkActionError.NO_COMMON_ACTIONS,
        selectedCases,
        templateVersionInfo,
      );
    }

    return createBaseState(
      {
        commonActions,
        canPerformBulkActions: true,
      },
      selectedCases,
      templateVersionInfo,
    );
  })();

  return bulkActionsState;
};

const findMatchingWorkflowRule = (
  workflowRules: Record<string, CaseTemplateWorkflowRuleOutput>,
  status: string,
  subStatus?: string | null,
): CaseTemplateWorkflowRuleOutput | undefined => {
  return Object.values(workflowRules).find((rule) =>
    isEqual(
      { status: rule.status, sub_status: rule.sub_status ?? null },
      { status, sub_status: subStatus ?? null },
    ),
  );
};

const getCaseActionKeys = (
  caseData: DeserializedQueueCase,
  workflowRules: Record<string, CaseTemplateWorkflowRuleOutput>,
): string[] => {
  const { status, sub_status: subStatus } = caseData.case_properties || {};
  if (!status) return [];

  const matchingRule = findMatchingWorkflowRule(
    workflowRules,
    status,
    subStatus,
  );
  return matchingRule?.actions?.map((action) => action.action_key) || [];
};

const extractCommonActions = (
  selectedCases: DeserializedQueueCase[],
  templateVersion:
    | { attributes?: CaseTemplateVersionAttributes }
    | null
    | undefined,
): CommonBulkAction[] => {
  const attributes = templateVersion?.attributes;

  if (!attributes?.workflow_rules || !attributes?.case_actions) {
    return [];
  }

  const workflowRules = attributes.workflow_rules as Record<
    string,
    CaseTemplateWorkflowRuleOutput
  >;
  const caseActions = attributes.case_actions as Record<
    string,
    CaseTemplateActionOutput
  >;

  const actionsPerCase = selectedCases.map((caseData) =>
    getCaseActionKeys(caseData, workflowRules),
  );

  if (actionsPerCase.length === 0) return [];

  const commonActionKeys = actionsPerCase.reduce(
    (common, caseActions) => intersection(common, caseActions),
    actionsPerCase[0],
  );

  return commonActionKeys
    .map((key) => {
      const action = caseActions[key];
      return action
        ? {
            key,
            display_name: action.display_name,
            description: action.description,
            action,
          }
        : null;
    })
    .filter((action): action is CommonBulkAction => action !== null)
    .sort((a, b) => (a.action.order ?? 0) - (b.action.order ?? 0));
};
